import { useGSAP } from '@gsap/react';
import classnames from 'classnames';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import _ from 'lodash';
import Markdown from 'markdown-to-jsx';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import qs from 'qs';
import React from 'react';
import Highlighter from 'react-highlight-words';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Modal from 'react-modal';
import { useClickAway } from 'react-use';
import useSWR from 'swr';

import MaxWidthContainer from 'components/MaxWidthContainer';
import Menubar from 'components/Menubar';
import MobileTabBar from 'components/MobileTabBar';
// import IndexBannerCarouselMobile from 'components/IndexBannerCarouselMobile';
// import IndexCommentCarouselMobile from 'components/IndexCommentCarouselMobile';
import CommentPopup from 'components/CommentPopup';
import Footer from 'components/Footer';
import {
  ChevronLeftSvg,
  CircleArrowSvg,
  CrossSvg,
  MicroscopeSvg,
  RankingSvg,
  TrashSvg,
} from 'components/SorraSvg';

// import { getProductOverallRanks } from 'utils/voting2024';
import { getProductOverallRanks } from 'utils/voting2025';

import { getProductWebpImageUrl, isStartDateEndDateValid } from 'utils';
import api, { swrFetcher } from 'utils/api';
import go_api, { swrGoFetcher } from 'utils/go_api';
import localStore from 'utils/localStore';
import { swrSorraAPIFetcher } from 'utils/wpApi';

import { MixpanelConfig } from 'utils/mixpanelConfig';
import { recordSearchToAnalytics } from 'utils/searchAnalytics';

import { useAppLaunch } from 'contexts/AppLaunch';
import { useLaunchData } from 'contexts/LaunchData';
import { useUserAuth } from 'contexts/UserAuth';

import useRWD from 'hooks/useRWD';

import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import HomeLandingInfo from 'components/HomeLandingInfo';

import HomeGroupBuy from 'components/HomeGroupBuy';
import HomeMiniBanner from 'components/HomeMiniBanner';
import HomeMonthlyRanking from 'components/HomeMonthlyRanking';
import HomeNewReviews from 'components/HomeNewReviews';
import HomePromoBanner from 'components/HomePromoBanner';
import HomeSignupSteps from 'components/HomeSignupSteps';
import MobileProductCard from 'components/MobileProductCard';
import ProductCard from 'components/ProductCard';
import WebpImg from 'components/WebpImg';
import BeautyAwardBadge from 'components/BeautyAwardBadge';
import MonthlyRankingBadge from 'components/MonthlyRankingBadge';

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

const { useState, useEffect, useCallback, useRef } = React;

const Home = (props) => {
  const { seo } = props;
  const { isLg } = useRWD();
  const { isSignInStatusReady, user, getAuthorizationHeader } = useUserAuth();

  const router = useRouter();
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const searchAreaRef = useRef(null);
  const searchContainerRef = useRef(null);

  const { isReady, pathname, query } = router;

  useEffect(() => {
    if (isReady) {
      if (!_.isNil(query.search)) {
        setInstantKeyword(query.search);
        searchInputRef.current.focus();
        const params = new URLSearchParams(query);
        params.delete('search');
        router.replace({ pathname, query: params.toString() }, undefined, {
          shallow: true,
        });
      } else if (!_.isNil(query.review)) {
        setIsCommentModalShow(true);
        const params = new URLSearchParams(query);
        params.delete('review');
        router.replace({ pathname, query: params.toString() }, undefined, {
          shallow: true,
        });
      }
    }
  }, [isReady, query]);

  const [menubarOpacity, setMenubarOpacity] = useState(0);
  // const [scrollPositionState, setScrollPositionState] = useState(0);

  useClickAway(searchAreaRef, () => {
    if (isLg) {
      setShowSearchModal(false);
      setSearchPlaceholder('');
      setShowClearHistory(false);
      searchInputRef.current.blur();
    }
  });

  useGSAP(() => {
    ScrollTrigger.create({
      scroller: '#app-wrapper',
      start: '1px',
      end: '+=190px',
      // markers: true,
      scrub: true,
      onUpdate: (self) => {
        setMenubarOpacity(self.progress);
      },
    });

    gsap.to('.search-container', {
      scrollTrigger: {
        scroller: '#app-wrapper',
        trigger: '.search-container',
        toggleActions: 'play none none none',
        // markers: true,
        start: isLg ? 'top 230px' : 'top 200px',
        end: '+=80px',
        scrub: true,
      },
      opacity: 0,
    });
    return () => {
      ScrollTrigger.killAll();
    };
  }, [isLg]);

  const { notifications, storeMap, getMonthlyRanks } = useAppLaunch();

  const { productOriginMap, secondCategories, secondCategoryMap } =
    useLaunchData();

  const carouselInterval = isLg ? 2000 : 1500;

  const { data: seoData, isLoading: isSeoLoading } = useSWR(
    '/api/v1/homepage/seo',
    swrGoFetcher,
    {
      fallbackData: {
        seo_question: '',
        seo_recommend: '',
      },
    }
  );

  const { data: promo_banners, isLoading: isBannerLoading } = useSWR(
    '/api/v1/homepage/banner/promo',
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const { data: promotions, isLoading: isPromotionsLoading } = useSWR(
    '/api/v1/homepage/promotions',
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const { data: allFeatures, isLoading: isAllFeaturesLoading } = useSWR(
    '/api/v1/features',
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  useEffect(() => {
    if (allFeatures) {
      allFeatures.forEach((feature, idx) => {
        if (feature?.type === 'sorra_featureds' && feature?.features != null) {
          setSorraFeaturedTab(feature);
          if (selectedSorraFeaturedProducts == '') {
            handleSorraFeaturedProduct(
              feature.type,
              feature.features[0].id,
              feature
            );
            setSelectedSorraFeatured(0);
          }
        }
        if (feature?.type === 'sorra_featured2s' && feature?.features != null) {
          setSorraFeaturedTab2(feature);
          if (selectedSorraFeaturedProducts2 == '') {
            handleSorraFeaturedProduct(
              feature.type,
              feature.features[0].id,
              feature
            );
            setSelectedSorraFeatured2(0);
          }
        }
      });
    }
  }, [allFeatures]);

  const handleSorraFeaturedProduct = async (field, id, featureData = null) => {
    const currentFeature =
      featureData ||
      (field == 'sorra_featureds' ? sorraFeaturedTab : sorraFeaturedTab2);
    if (!isAllFeaturesLoading) {
      const result = await go_api.get(
        `/api/v1/features/${field}/${id}/products`
      );
      const products = result.data.data;
      let mobileProducts = result.data.data;
      if (currentFeature?.banner) {
        const { starts_at, ends_at } = currentFeature.banner;
        if (isStartDateEndDateValid(starts_at, ends_at)) {
          const banner = {
            ...currentFeature.banner,
            is_banner: true,
          };
          mobileProducts = [banner].concat(mobileProducts);
          if (products.length >= 5) {
            products.splice(4, 0, banner);
          } else {
            products.splice(products.length + 1, 0, banner);
          }
        }
      }

      switch (field) {
        case 'sorra_featureds':
          setSelectedSorraFeaturedProducts(products);
          setChunkSorraFeaturedProducts(_.chunk(products, 5));
          setChunkMobileSorraFeaturedProducts(_.chunk(mobileProducts, 2));
          break;
        case 'sorra_featured2s':
          setSelectedSorraFeaturedProducts2(products);
          setChunkSorraFeaturedProducts2(_.chunk(products, 5));
          setChunkMobileSorraFeaturedProducts2(_.chunk(mobileProducts, 2));
          break;
      }
    } else {
      switch (field) {
        case 'sorra_featureds':
          setSelectedSorraFeaturedProducts([]);
          setChunkSorraFeaturedProducts([]);
          setChunkMobileSorraFeaturedProducts([]);
          break;
        case 'sorra_featured2s':
          setSelectedSorraFeaturedProducts2([]);
          setChunkSorraFeaturedProducts2([]);
          setChunkMobileSorraFeaturedProducts2([]);
          break;
      }
    }
  };

  const [localSearchHistory, setLocalSearchHistory] = useState([]);
  useEffect(() => {
    if (isSignInStatusReady && !user) {
      const h = localStore.getSorraSearchHistory();
      setLocalSearchHistory(h);
    }
  }, [isSignInStatusReady, user]);

  const {
    data: searchHistory,
    mutate: mutateSearchHistory,
    isLoading: isSearchHistoryLoading,
  } = useSWR(
    user ? '/api/v3/search/history' : null,
    async (url) => {
      const r = await swrFetcher(url, getAuthorizationHeader);
      return _.filter(r, (h) => {
        return !_.isEmpty(_.trim(h));
      });
    },
    {
      fallbackData: localSearchHistory,
    }
  );

  const { data: topSearches, isLoading: isTopSearchesLoading } = useSWR(
    '/api/v3/search/topSearches',
    async (url) => {
      const data = await swrFetcher(url);
      return _.take(data, isLg ? 12 : 8);
    },
    {
      fallbackData: [],
    }
  );

  const [keyword, setKeyword] = useState('');
  const searchQuery = qs.stringify({
    keyword: _.trim(keyword),
  });
  const [instantKeyword, setInstantKeyword] = useState('');
  const debouncedSearchKeyword = useCallback(
    _.debounce((value) => {
      setKeyword(value);
    }, 300),
    [setKeyword]
  );

  useEffect(() => {
    debouncedSearchKeyword(instantKeyword);
  }, [instantKeyword]);
  const { data: recommendations, isLoading: isRecommendationsLoading } = useSWR(
    `/api/v3/search/recommendations?${searchQuery}`,
    swrFetcher,
    {
      fallbackData: [],
    }
  );

  const recommendationSecondCategories = _.reduce(
    recommendations,
    (acc, rec) => {
      const { second_categories } = rec;
      return _.uniq([...acc, ...second_categories]);
    },
    []
  );

  const [topSearchDisplayIdx, setTopSearchDisplayIdx] = useState(0);
  const [topSearchDisplayCount, setTopSearchDisplayCount] = useState(0);

  const [searchPlaceholder, setSearchPlaceholder] = useState('');

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showClearHistory, setShowClearHistory] = useState(false);

  useEffect(() => {
    setTopSearchDisplayCount(topSearches.length);
  }, [topSearches]);

  useEffect(() => {
    setTopSearchDisplayIdx(0);
    if (topSearchDisplayCount > 0) {
      if (showSearchModal && _.isEmpty(searchPlaceholder)) {
        setSearchPlaceholder(topSearches[0]);
      }
    }
  }, [topSearchDisplayCount]);

  useEffect(() => {
    if (
      topSearchDisplayIdx === 0 &&
      gsap.getProperty('.search-display', 'y') !== 0
    ) {
      gsap.to('.search-display', {
        y: -32 * topSearchDisplayCount,
        duration: 0.4,
        onComplete: () => {
          gsap.set('.search-display', {
            y: 0,
          });
        },
      });
    } else {
      gsap.to('.search-display', {
        y: -32 * topSearchDisplayIdx,
        duration: 0.4,
      });
    }
  }, [topSearchDisplayIdx, topSearchDisplayCount]);

  useEffect(() => {
    let interval = null;
    if (!showSearchModal) {
      interval = setInterval(() => {
        setTopSearchDisplayIdx((prev) => {
          return (prev + 1) % topSearchDisplayCount;
        });
      }, 3000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showSearchModal, topSearchDisplayCount]);

  const { seo_question, seo_recommend } = seoData;

  const { data: newActivities, isLoading: isActivitiesLoading } = useSWR(
    '/api/v1/homepage/activities',
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const { data: miniBanners, isLoading: isMiniBannersLoading } = useSWR(
    '/api/v1/homepage/banner/mini',
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const { data: new_reviews, isLoading: isNewReviewsLoading } = useSWR(
    '/api/v1/reviews?limit=20',
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const { data: rewards, isLoading: isRewardsLoading } = useSWR(
    '/api/v1/homepage/rewards',
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const { data: posts, isLoading: isPostsLoading } = useSWR(
    'v1/wordpress/posts?page=1&per_page=10',
    swrSorraAPIFetcher,
    { fallbackData: [] }
  );

  const [activeMiniBannerIndex, setActiveMiniBannerIndex] = useState(0);

  // sorra featured
  const [sorraFeaturedTab, setSorraFeaturedTab] = useState({});
  const [selectedSorraFeaturedProducts, setSelectedSorraFeaturedProducts] =
    useState([]);
  const [chunkSorraFeaturedProducts, setChunkSorraFeaturedProducts] = useState(
    []
  );
  const [selectedSorraFeatured, setSelectedSorraFeatured] = useState(null);
  const [
    chunkMobileSorraFeaturedProducts,
    setChunkMobileSorraFeaturedProducts,
  ] = useState([]);

  // sorra featured2
  const [sorraFeaturedTab2, setSorraFeaturedTab2] = useState({});
  const [selectedSorraFeaturedProducts2, setSelectedSorraFeaturedProducts2] =
    useState([]);
  const [chunkSorraFeaturedProducts2, setChunkSorraFeaturedProducts2] =
    useState([]);
  const [selectedSorraFeatured2, setSelectedSorraFeatured2] = useState(null);
  const [
    chunkMobileSorraFeaturedProducts2,
    setChunkMobileSorraFeaturedProducts2,
  ] = useState([]);

  const [bottomSuggestionsExpanded, setBottomSuggestionsExpanded] =
    useState(false);
  const [bottomFaqExpanded, setBottomFaqExpanded] = useState(false);

  const [isCommentModalShow, setIsCommentModalShow] = useState(false);

  useEffect(() => {
    if (showSearchModal) {
      document.querySelector('#app-wrapper').scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      document.querySelector('#app-wrapper').style.overflow = 'hidden';
    } else {
      document.querySelector('#app-wrapper').style.overflow = '';
    }
  }, [showSearchModal]);

  const routeToSearchPage = (keyword, secondCategoryId) => {
    if (!_.isEmpty(keyword)) {
      const mo = [keyword];
      const q = { keyword };
      if (!_.isNil(secondCategoryId) && secondCategoryMap[secondCategoryId]) {
        const sc = secondCategoryMap[secondCategoryId];
        q.categoryID = sc.id;
        q.category = sc.name;
        mo.unshift(sc.name);
      }
      // setShowSearchModal(false);
      MixpanelConfig.track('search_performed', { search: mo.join(' • ') });
      // 只有這裡算「使用者主動送出的搜尋」，才計入 Algolia
      recordSearchToAnalytics(keyword);
      router.push(`/search?${qs.stringify(q)}`);
    }
  };

  const handleSearchEnter = () => {
    if (!keyword && topSearches[topSearchDisplayIdx]) {
      routeToSearchPage(topSearches[topSearchDisplayIdx]);
    } else if (!isRecommendationsLoading && recommendations.length) {
      routeToSearchPage(keyword);
    }
  };

  const handleClearSearchHistory = async () => {
    if (user) {
      const headers = await getAuthorizationHeader();
      await api.request({
        method: 'DELETE',
        url: '/api/v3/search/history',
        headers,
      });
      mutateSearchHistory();
      setShowClearHistory(false);
    } else {
      localStore.deleteSorraSearchHistory();
      setLocalSearchHistory([]);
    }
  };

  return (
    <div>
      <NextSeo title={seo.title} description={seo.description} />
      <Modal
        isOpen={showClearHistory && !isLg}
        shouldCloseOnOverlayClick={false}
        shouldFocusAfterRender={false}
        shouldReturnFocusAfterClose={false}
        className="flex h-full w-full items-center justify-center"
        overlayClassName="z-[65] absolute top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
      >
        <div className="relative rounded-2xl bg-white px-4 py-6">
          <button
            className="absolute right-4 top-4 h-5 w-5"
            onClick={() => {
              setShowClearHistory(false);
            }}
          >
            <CrossSvg />
          </button>
          <p className="px-[80px] text-center text-base font-medium text-[#111928]">
            刪除全部歷史搜索
          </p>
          <div className="mt-9 flex h-12 w-full gap-x-2 text-base font-medium">
            <button
              className="flex flex-1 items-center justify-center rounded-lg border border-sorra-pink bg-white text-sorra-pink"
              onClick={() => {
                setShowClearHistory(false);
              }}
            >
              取消
            </button>
            <button
              className="flex flex-1 items-center justify-center rounded-lg bg-sorra-pink text-white"
              onClick={async () => {
                await handleClearSearchHistory();
                setShowClearHistory(false);
              }}
            >
              確認
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showSearchModal}
        shouldCloseOnOverlayClick={false}
        shouldFocusAfterRender={false}
        shouldReturnFocusAfterClose={false}
        className="p-safe-offset block h-full w-full bg-white lg:hidden"
        overlayClassName="z-[60] lg:z-[49] absolute flex items-center justify-center top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
        onAfterOpen={() => {
          if (!isLg) {
            mobileSearchInputRef.current.focus();
          }
        }}
      >
        <div className="relative flex h-full w-full flex-col py-6">
          <div className="flex h-12 w-full shrink-0 items-center gap-x-2 px-4">
            <button
              className="h-6 w-6 shrink-0"
              onClick={() => {
                setShowSearchModal(false);
                setSearchPlaceholder('');
              }}
            >
              <ChevronLeftSvg />
            </button>
            <div className="flex h-full grow items-center gap-x-4 rounded-lg border border-[#D1D5DB] pl-4 pr-2">
              <div className="h-6 w-6">
                <MicroscopeSvg />
              </div>
              <input
                ref={mobileSearchInputRef}
                className="w-full bg-transparent text-base font-medium text-[#111928] placeholder:text-[#9B9B9B] focus:outline-none"
                placeholder={searchPlaceholder}
                value={instantKeyword}
                onChange={(e) => {
                  setInstantKeyword(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.keyCode === 13) {
                    handleSearchEnter();
                  }
                }}
              ></input>
              {keyword && (
                <button
                  className="h-6 w-6"
                  onClick={() => {
                    setInstantKeyword('');
                  }}
                >
                  <CrossSvg color="#9B9B9B" />
                </button>
              )}
            </div>
          </div>
          <div className="no-scrollbar mt-6 min-h-0 w-full grow overflow-hidden">
            <div>
              {keyword && isRecommendationsLoading && (
                <div className="flex w-full items-center justify-center">
                  <img
                    className="h-[145px] w-[145px]"
                    src="/images/search-placeholder.gif"
                  ></img>
                </div>
              )}
              {keyword &&
                !isRecommendationsLoading &&
                recommendations.length === 0 && (
                  <div className="mt-6 flex w-full flex-col items-center justify-center gap-y-3">
                    <img
                      className="h-[73px] w-[73px]"
                      src="/svgs/search-empty.svg"
                    ></img>
                    <div className="font-medium text-sorra-gray-800">
                      沒有搜索結果...
                    </div>
                  </div>
                )}
              {!isRecommendationsLoading && recommendations.length > 0 && (
                <div className="pb-8">
                  <div className="flex flex-col gap-y-6 px-4 pb-3 text-sm font-medium text-[#4a4a4a]">
                    {_.take(recommendations, 5).map((recommendation, idx) => {
                      const { id, brand, name, en_name, matchedWords } =
                        recommendation;
                      const display = `${brand ? `${brand} ` : ''} ${name || en_name}`;
                      const overallRank = _.head(getProductOverallRanks(id));
                      const monthlyRank = _.head(getMonthlyRanks(id));
                      return (
                        <div
                          className="flex cursor-pointer flex-row flex-wrap items-center gap-x-2 gap-y-1"
                          onClick={async () => {
                            const newKeyword = display;
                            if (user) {
                              try {
                                const headers = await getAuthorizationHeader();
                                await api.request({
                                  method: 'post',
                                  url: '/api/v3/search/history',
                                  headers,
                                  data: {
                                    keyword: newKeyword,
                                  },
                                });
                              } catch (_) {
                                // console.error(error);
                              }
                            } else {
                              localStore.addSorraSearchHistory(newKeyword);
                            }
                            window.location.href = `/product/${id}`;
                          }}
                        >
                          <Highlighter
                            highlightClassName="text-sorra-pink bg-transparent"
                            textToHighlight={display}
                            searchWords={matchedWords}
                          />
                          {overallRank && (
                            <BeautyAwardBadge
                              size="s"
                              year={overallRank.year}
                              rank={overallRank.rank}
                              category={overallRank.name}
                            />
                          )}
                          {monthlyRank && (
                            <MonthlyRankingBadge
                              size="s"
                              year={monthlyRank.year}
                              month={monthlyRank.month}
                              rank={monthlyRank.rank}
                              category={monthlyRank.category}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="h-px w-full bg-[#E7E7E7]" />
                  <div className="mt-4 px-4">
                    <p>
                      在分類下搜索關鍵字{' '}
                      <span className="font-medium text-sorra-pink">
                        “{keyword}”
                      </span>
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-y-6">
                      {_.take(recommendationSecondCategories, 6).map(
                        (secondCategoryId) => {
                          const secondCategoryName = secondCategoryMap[
                            secondCategoryId
                          ]
                            ? secondCategoryMap[secondCategoryId].name
                            : '';
                          return (
                            <div
                              key={secondCategoryId}
                              className="flex h-7 cursor-pointer items-center gap-x-1.5 rounded-full text-sm font-medium text-[#111928]"
                              onClick={() => {
                                routeToSearchPage(keyword, secondCategoryId);
                                // window.location.href = `/search?product=&category=${secondCategoryName}&categoryID=${secondCategoryId}&keyword=${keyword}`;
                              }}
                            >
                              <div className="h-4 w-4">
                                <CircleArrowSvg />
                              </div>
                              <p>{secondCategoryName}</p>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!keyword && (
              <div className="px-4">
                {searchHistory.length > 0 && (
                  <div>
                    <div className="flex justify-between text-base">
                      <p className="font-medium text-[#111928]">搜尋記錄</p>
                      <div className="flex gap-x-3">
                        <button
                          className="flex items-center gap-x-1 font-medium text-[#9b9b9b]"
                          onClick={() => {
                            setShowClearHistory(true);
                          }}
                        >
                          <div className="h-5 w-5">
                            <TrashSvg />
                          </div>
                          <p>清除</p>
                        </button>
                      </div>
                    </div>
                    <div className="flex-rwo mt-4 flex flex-wrap gap-x-1.5 gap-y-2">
                      {searchHistory.map((search, idx) => {
                        return (
                          <div
                            key={idx}
                            className="min-w-0 cursor-pointer items-center overflow-hidden truncate rounded-full border border-[#E7E7E7] px-3 py-1 text-xs font-medium text-[#4a4a4a]"
                            onClick={() => {
                              // setShowSearchModal(false);
                              // setSearchPlaceholder('');
                              routeToSearchPage(search);
                            }}
                          >
                            {search}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div
                  className={classnames({
                    'mt-6': searchHistory.length > 0,
                  })}
                >
                  <div className="flex justify-between">
                    <p className="font-medium text-[#111928]">🔥 熱門關鍵字</p>
                    <button
                      className="flex items-center gap-x-1 font-medium text-sorra-pink"
                      onClick={() => {
                        router.push('/ranking');
                        // window.location.href = '/ranking';
                      }}
                    >
                      <div className="h-5 w-5">
                        <RankingSvg />
                      </div>
                      <p>美妝排行榜</p>
                    </button>
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-y-6">
                    {topSearches.map((search, idx) => {
                      return (
                        <div
                          key={idx}
                          className="flex h-6 cursor-pointer items-center gap-x-1 rounded-full text-sm font-medium text-[#111928]"
                          onClick={() => {
                            routeToSearchPage(search);
                            // router.push(`/search?keyword=${search}`);
                          }}
                        >
                          <div className="h-4 w-4">
                            <CircleArrowSvg />
                          </div>
                          <p>{search}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
      <main>
        <Menubar
          props={{
            homeExtend: true,
            // scrollPosition: menubarOpacity,
            sourceUI: 'Home',
          }}
          menubarOpacity={menubarOpacity}
          pressSearch={() => {
            // pressSearchProductInputFocus(true);
            searchInputRef.current.focus();
          }}
        />

        <div className="overflow-x-hidden">
          <div
            className={
              "index-header relative bg-[url('/images/bg-header-new_mobile.jpg')] bg-cover bg-center lg:bg-[url('/images/bg-header-new.jpeg')]" +
              (notifications?.length > 0 ? ' withNoti' : '')
            }
          >
            <MaxWidthContainer
              className={
                'index-header-maxWidthContainer relative flex h-full items-start justify-between' +
                (notifications?.length > 0 ? ' withNoti' : '')
              }
            >
              <div className="index-header-captions relative flex w-full grid-rows-1 flex-col items-center justify-center gap-y-2 lg:gap-y-4">
                <p className="text-center text-2xl font-bold lg:text-5xl">
                  Buy Less, Choose Well.
                </p>
                <p className="text-center text-base font-medium lg:text-2xl">
                  最真實美妝評論平台
                </p>
              </div>

              {/* search bar */}
              <div
                ref={searchContainerRef}
                className={classnames(
                  'search-container absolute bottom-0 left-0 right-0 flex w-full -translate-y-6 flex-col items-center lg:translate-y-[calc(100%-40px)]',
                  {
                    'z-0': !showSearchModal,
                    'z-50': showSearchModal,
                  }
                )}
              >
                <div
                  ref={searchAreaRef}
                  className="relative w-full max-w-screen-lg px-4 lg:px-0"
                >
                  <div
                    className={classnames(
                      'relative flex h-12 w-full items-center gap-x-4 rounded-lg bg-white p-4 shadow-lg lg:h-20',
                      {
                        'border border-sorra-pink': showSearchModal,
                      }
                    )}
                  >
                    <div className="flex min-w-0 grow items-center">
                      <div className="ml-0 h-6 w-6 lg:ml-4">
                        <MicroscopeSvg />
                      </div>
                      <div className="relative h-8 min-w-0 grow">
                        <div className="pointer-events-none absolute bottom-0 left-0 right-0 top-0 overflow-hidden">
                          <div
                            className={classnames(
                              'search-display absolute left-4 top-0 text-base font-medium text-[#9B9B9B]',
                              {
                                hidden:
                                  showSearchModal || !_.isEmpty(instantKeyword),
                              }
                            )}
                          >
                            {topSearches.map((search, idx) => {
                              return (
                                <div
                                  key={idx}
                                  className="flex h-8 items-center"
                                >
                                  <p>{search}</p>
                                </div>
                              );
                            })}
                            {topSearches.length > 0 && (
                              <div className="flex h-8 items-center">
                                <p>{topSearches[0]}</p>
                              </div>
                            )}
                            {/* topSearches.length */}
                          </div>
                        </div>
                        <input
                          ref={searchInputRef}
                          className="search-input h-full w-full bg-transparent pl-4 text-base font-medium text-[#111928] placeholder:text-[#9B9B9B] focus:outline-none"
                          placeholder={showSearchModal ? searchPlaceholder : ''}
                          value={instantKeyword}
                          onChange={(e) => {
                            setInstantKeyword(e.target.value);
                          }}
                          onFocus={() => {
                            setSearchPlaceholder(
                              _.get(topSearches, topSearchDisplayIdx) || ''
                            );
                            setShowSearchModal(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.keyCode === 13) {
                              handleSearchEnter();
                            }
                          }}
                          // onBlur={() => {}}
                        ></input>
                      </div>
                    </div>
                    <button
                      className={classnames(
                        'hidden items-center gap-x-3 rounded-lg bg-sorra-pink px-8 py-3 font-medium text-white lg:flex',
                        {
                          '!bg-[#9B9B9B]':
                            keyword &&
                            !isRecommendationsLoading &&
                            recommendations.length === 0,
                        }
                      )}
                      disabled={
                        keyword &&
                        !isRecommendationsLoading &&
                        recommendations.length === 0
                      }
                      onClick={() => {
                        handleSearchEnter();
                      }}
                    >
                      <div className="h-5 w-5">
                        <MicroscopeSvg color="white" />
                      </div>
                      <p>搜索</p>
                    </button>
                  </div>
                  <div
                    className={classnames(
                      'mt-4 hidden overflow-hidden rounded-lg border border-[#E7E7E7] bg-white lg:block',
                      {
                        '!hidden': !showSearchModal,
                      }
                    )}
                  >
                    <div>
                      {keyword && isRecommendationsLoading && (
                        <div className="flex w-full items-center justify-center py-11">
                          <img
                            className="h-[145px] w-[145px]"
                            src="/images/search-placeholder.gif"
                          ></img>
                        </div>
                      )}
                      {keyword &&
                        !isRecommendationsLoading &&
                        recommendations.length === 0 && (
                          <div className="flex w-full flex-col items-center justify-center gap-y-3 py-11">
                            <img
                              className="h-[73px] w-[73px]"
                              src="/svgs/search-empty.svg"
                            ></img>
                            <div className="font-medium text-sorra-gray-800">
                              沒有搜索結果...
                            </div>
                          </div>
                        )}
                      {!isRecommendationsLoading &&
                        recommendations.length > 0 && (
                          <div className="pb-8 pt-4">
                            <div className="flex flex-col gap-y-4 px-8 pb-4 text-sm font-medium text-[#4a4a4a]">
                              {_.take(recommendations, 5).map(
                                (recommendation, idx) => {
                                  const {
                                    id,
                                    brand,
                                    name,
                                    en_name,
                                    matchedWords,
                                  } = recommendation;
                                  const display = `${brand ? `${brand} ` : ''} ${name || en_name}`;
                                  const overallRank = _.head(
                                    getProductOverallRanks(id)
                                  );
                                  const monthlyRank = _.head(
                                    getMonthlyRanks(id)
                                  );
                                  return (
                                    <div
                                      className="flex cursor-pointer flex-row items-center gap-x-2"
                                      onClick={async () => {
                                        const newKeyword = display;
                                        if (user) {
                                          try {
                                            const headers =
                                              await getAuthorizationHeader();
                                            await api.request({
                                              method: 'post',
                                              url: '/api/v3/search/history',
                                              headers,
                                              data: {
                                                keyword: newKeyword,
                                              },
                                            });
                                          } catch (_) {
                                            // console.error(error);
                                          }
                                        } else {
                                          localStore.addSorraSearchHistory(
                                            newKeyword
                                          );
                                        }
                                        window.location.href = `/product/${id}`;
                                      }}
                                    >
                                      <Highlighter
                                        highlightClassName="text-sorra-pink bg-transparent"
                                        textToHighlight={display}
                                        searchWords={matchedWords}
                                      />
                                      {overallRank && (
                                        <BeautyAwardBadge
                                          size="s"
                                          year={overallRank.year}
                                          rank={overallRank.rank}
                                          category={overallRank.name}
                                        />
                                      )}
                                      {monthlyRank && (
                                        <MonthlyRankingBadge
                                          size="s"
                                          year={monthlyRank.year}
                                          month={monthlyRank.month}
                                          rank={monthlyRank.rank}
                                          category={monthlyRank.category}
                                        />
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                            <div className="h-px w-full bg-[#E7E7E7]" />
                            <div className="mt-2 px-8">
                              <p>
                                在分類下搜索關鍵字{' '}
                                <span className="font-medium text-sorra-pink">
                                  “{keyword}”
                                </span>
                              </p>
                              <div className="mt-4 grid grid-cols-2 gap-x-2 gap-y-4">
                                {_.take(recommendationSecondCategories, 6).map(
                                  (secondCategoryId) => {
                                    const secondCategoryName =
                                      secondCategoryMap[secondCategoryId]
                                        ? secondCategoryMap[secondCategoryId]
                                            .name
                                        : '';
                                    return (
                                      <div
                                        key={secondCategoryId}
                                        className="flex h-7 cursor-pointer items-center gap-x-1.5 gap-y-4 rounded-full text-sm font-medium text-[#111928]"
                                        onClick={() => {
                                          routeToSearchPage(
                                            keyword,
                                            secondCategoryId
                                          );
                                          // router.push(
                                          //   `/search?product=&category=${secondCategoryName}&categoryID=${secondCategoryId}&keyword=${keyword}`
                                          // );
                                          // window.location.href = `/search?product=&category=${secondCategoryName}&categoryID=${secondCategoryId}&keyword=${keyword}`;
                                        }}
                                      >
                                        <div className="h-[13px] w-[14px]">
                                          <CircleArrowSvg />
                                        </div>
                                        <p>{secondCategoryName}</p>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                    {!keyword && (
                      <div className="px-8 pb-8 pt-5">
                        {searchHistory.length > 0 && (
                          <div>
                            <div className="flex justify-between text-base">
                              <p className="font-medium text-[#111928]">
                                搜尋記錄
                              </p>
                              <div className="flex gap-x-3">
                                {showClearHistory && (
                                  <>
                                    <button
                                      className="flex items-center gap-x-1 font-medium text-[#9b9b9b]"
                                      onClick={() => {
                                        setShowClearHistory(false);
                                      }}
                                    >
                                      <p>取消</p>
                                    </button>
                                    <div className="h-6 w-px border border-[#9b9b9b] bg-[#9b9b9b]" />
                                    <button
                                      className="flex items-center gap-x-1 font-medium text-sorra-pink"
                                      onClick={async () => {
                                        await handleClearSearchHistory();
                                      }}
                                    >
                                      <div className="h-5 w-5">
                                        <TrashSvg color="#F16B7C" />
                                      </div>
                                      <p>確認清除</p>
                                    </button>
                                  </>
                                )}
                                {!showClearHistory && (
                                  <button
                                    className="flex items-center gap-x-1 font-medium text-[#9b9b9b]"
                                    onClick={() => {
                                      setShowClearHistory(true);
                                    }}
                                  >
                                    <div className="h-5 w-5">
                                      <TrashSvg />
                                    </div>
                                    <p>清除</p>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex-rwo mt-4 flex flex-wrap gap-2">
                              {searchHistory.map((search, idx) => {
                                return (
                                  <div
                                    key={idx}
                                    className="block cursor-pointer rounded-full border border-[#E7E7E7] px-3 py-1 text-sm font-medium text-[#4a4a4a]"
                                    onClick={() => {
                                      routeToSearchPage(search);
                                      // router.push(`/search?keyword=${search}`);
                                      // window.location.href = `/search?keyword=${search}`;
                                    }}
                                  >
                                    <p>{search}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div
                          className={classnames({
                            'mt-6': searchHistory.length > 0,
                          })}
                        >
                          <div className="flex justify-between">
                            <p className="font-medium text-[#111928]">
                              🔥 熱門關鍵字
                            </p>
                            <button
                              className="flex items-center gap-x-1 font-medium text-sorra-pink"
                              onClick={() => {
                                router.push('/ranking');
                                // window.location.href = '/ranking';
                              }}
                            >
                              <div className="h-5 w-5">
                                <RankingSvg />
                              </div>
                              <p>美妝排行榜</p>
                            </button>
                          </div>
                          <div className="mt-4 grid grid-cols-4 gap-x-2 gap-y-4">
                            {topSearches.map((search, idx) => {
                              return (
                                <div
                                  key={idx}
                                  className="flex h-7 cursor-pointer items-center gap-x-1.5 gap-y-4 rounded-full px-0.5 text-sm font-medium text-[#111928]"
                                  onClick={() => {
                                    routeToSearchPage(search);
                                    // router.push(`/search?keyword=${search}`);
                                    // window.location.href = `/search?keyword=${search}`;
                                  }}
                                >
                                  <div className="h-[13px] w-[14px]">
                                    <CircleArrowSvg />
                                  </div>
                                  <p>{search}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </MaxWidthContainer>
          </div>
          <MaxWidthContainer>
            <div className="gap-x-auto mx-6 block flex flex-wrap justify-between pb-4 pt-6 text-center lg:hidden">
              <Link
                prefetch={false}
                href="/ingredients"
                className="hover:text-sorra-pink"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="rounded-all relative flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-[#F5F5F5]">
                    <img
                      src="/images/icons/mobile_ai.svg"
                      className="h-[29px] w-[29px] object-contain"
                    />
                  </div>
                  <p className="text-sm">AI成份分析</p>
                </div>
              </Link>
              <Link
                prefetch={false}
                href="/rewards"
                className="hover:text-sorra-pink"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="rounded-all relative flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-[#F5F5F5]">
                    <img
                      src="/images/icons/mobile_gift.svg"
                      className="h-[29px] w-[29px] object-contain"
                    />
                    <img
                      src="/images/icons/vip.png"
                      className="absolute right-2 top-2 h-[13px] w-[22px] -translate-y-[100%] translate-x-[80%] object-contain"
                    />
                  </div>
                  <p className="text-sm">兌換禮物</p>
                </div>
              </Link>
              <Link
                prefetch={false}
                href="/campaign"
                className="hover:text-sorra-pink"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="rounded-all relative flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-[#F5F5F5]">
                    <img
                      src="/images/icons/mobile_trail.svg"
                      className="h-[29px] w-[29px] object-contain"
                    />
                    <img
                      src="/images/icons/vip.png"
                      className="absolute right-2 top-2 h-[13px] w-[22px] -translate-y-[100%] translate-x-[80%] object-contain"
                    />
                  </div>
                  <p className="text-sm">試用活動</p>
                </div>
              </Link>
              <Link
                prefetch={false}
                href={`${user ? '/profile' : ''}/memberscontent`}
                className="hover:text-sorra-pink"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="rounded-all relative flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-[#F5F5F5]">
                    <img
                      src="/images/icons/mobile_premium.svg"
                      className="h-[29px] w-[29px] object-contain"
                    />
                    <img
                      src="/images/icons/vip.png"
                      className="absolute right-2 top-2 h-[13px] w-[22px] -translate-y-[100%] translate-x-[80%] object-contain"
                    />
                  </div>
                  <p className="text-sm">會員專屬內容</p>
                </div>
              </Link>
            </div>
          </MaxWidthContainer>
          {/* promoBanners + groupBuy: mobile時groupBuy排在banner前面 */}
          <div className="flex flex-col">
            <div className="order-1 lg:order-2">
              <HomeGroupBuy />
            </div>
            <div className="order-2 lg:order-1">
              <HomePromoBanner
                isLoading={isBannerLoading}
                banners={promo_banners}
              />
            </div>
          </div>

          {/* 每月排行榜 */}
          <HomeMonthlyRanking />

          {/* sorraFeature1 */}
          {(miniBanners.length > 0 || isMiniBannersLoading) && (
            <div
              className={
                'mt-6 lg:mt-12' +
                (miniBanners.length == 0 && isMiniBannersLoading == false
                  ? ' hidden'
                  : '')
              }
            >
              <div>
                <MaxWidthContainer className="px-4 lg:px-0">
                  <div className="no-scrollbar mt-6 flex w-full flex-row gap-x-8 overflow-x-auto">
                    {sorraFeaturedTab.features &&
                      sorraFeaturedTab.features.map((tab, idx) => {
                        return (
                          <div
                            key={tab.id}
                            className="flex shrink-0 cursor-pointer flex-col gap-y-4"
                            onClick={() => {
                              setSelectedSorraFeatured(idx);
                              handleSorraFeaturedProduct(
                                'sorra_featureds',
                                tab.id,
                                ''
                              );
                            }}
                          >
                            <p
                              className={classnames(
                                'text-base lg:text-xl',
                                idx === selectedSorraFeatured
                                  ? 'font-bold text-[#111928]'
                                  : 'font-normal text-[#4a4a4a]'
                              )}
                            >
                              {tab.name}
                            </p>
                            <div
                              className={classnames(
                                'h-[3px] rounded-full',
                                idx === selectedSorraFeatured
                                  ? 'bg-sorra-pink'
                                  : 'bg-transparent'
                              )}
                            />
                          </div>
                        );
                      })}
                  </div>
                </MaxWidthContainer>
              </div>
              <div className="h-px w-full bg-gray-100"></div>
              <MaxWidthContainer
                key={selectedSorraFeatured}
                className="overflow-hidden px-4 pt-5 lg:px-0 lg:pt-8"
              >
                <div className="mini-banner-carousel relative hidden lg:block">
                  {isAllFeaturesLoading ? (
                    <Carousel
                      swipeable={false}
                      emulateTouch={false}
                      stopOnHover={false}
                      showIndicators={false}
                      showStatus={false}
                      showThumbs={false}
                    >
                      <div className="grid grid-cols-4 gap-6">
                        {[0, 1, 2, 3].map((i) => {
                          return (
                            <div key={i} className="aspect-[302/521]">
                              <div className="h-full w-full overflow-hidden rounded-[10px] bg-[#FFFFFF]">
                                <Skeleton width={'100%'} height={'100%'} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Carousel>
                  ) : (
                    selectedSorraFeaturedProducts.length > 0 && (
                      <Carousel
                        swipeable={true}
                        emulateTouch={true}
                        stopOnHover={true}
                        showIndicators={false}
                        showStatus={false}
                        showThumbs={false}
                        renderArrowPrev={(clickHandler, hasPrev, labelPrev) => {
                          return (
                            hasPrev && (
                              <button
                                onClick={clickHandler}
                                className="absolute -left-6 top-1/2 z-10 h-10 w-10 -translate-x-full -translate-y-1/2 rounded-full"
                                style={{
                                  boxShadow:
                                    '0px 5px 7.5px -1.25px rgba(0, 0, 0, 0.1), 0px 2.5px 5px -2.5px rgba(0, 0, 0, 0.05)',
                                }}
                              >
                                <img
                                  className="p-2.5"
                                  src="/svgs/index-horizontalScroll-arrowLeft.svg"
                                ></img>
                              </button>
                            )
                          );
                        }}
                        renderArrowNext={(clickHandler, hasNext, labelNext) => {
                          return (
                            hasNext && (
                              <button
                                onClick={clickHandler}
                                className="absolute -right-6 top-1/2 z-10 h-10 w-10 -translate-y-1/2 translate-x-full rounded-full"
                                style={{
                                  boxShadow:
                                    '0px 5px 7.5px -1.25px rgba(0, 0, 0, 0.1), 0px 2.5px 5px -2.5px rgba(0, 0, 0, 0.05)',
                                }}
                              >
                                <img
                                  className="p-2.5"
                                  src="/svgs/index-horizontalScroll-arrowRight.svg"
                                ></img>
                              </button>
                            )
                          );
                        }}
                      >
                        {chunkSorraFeaturedProducts.map((p, idx) => {
                          return (
                            <div key={idx} className="grid grid-cols-5 gap-6">
                              {p.map((chunk, chunkIdx) => {
                                return (
                                  <ProductCard
                                    key={`product-${chunk.id}-${idx}-${chunkIdx}`}
                                    p={chunk}
                                    productOriginMap={productOriginMap}
                                    storeMap={storeMap}
                                    idx={idx}
                                    chunkIdx={chunkIdx}
                                  />
                                );
                              })}
                            </div>
                          );
                        })}
                      </Carousel>
                    )
                  )}
                </div>
                <div className="mobile-mini-banner-carousel relative block w-full lg:hidden">
                  {isAllFeaturesLoading ? (
                    <Carousel
                      swipeable={false}
                      emulateTouch={false}
                      stopOnHover={false}
                      showIndicators={false}
                      showStatus={false}
                      showThumbs={false}
                    >
                      <div className="grid grid-cols-2 gap-2.5 pr-2.5">
                        {[0, 1].map((i) => {
                          return (
                            <div key={i} className="aspect-[140/342]">
                              <div className="h-full w-full overflow-hidden rounded-[10px] bg-[#FFFFFF]">
                                <Skeleton width={'100%'} height={'100%'} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Carousel>
                  ) : (
                    chunkMobileSorraFeaturedProducts.length > 0 && (
                      <Carousel
                        swipeable={true}
                        emulateTouch={true}
                        stopOnHover={true}
                        showIndicators={false}
                        showStatus={false}
                        showThumbs={false}
                        showArrows={false}
                      >
                        {chunkMobileSorraFeaturedProducts.map((p, idx) => {
                          return (
                            <div
                              key={idx}
                              className="grid grid-cols-2 gap-2.5 pr-2.5"
                            >
                              {p.map((chunk, chunkIdx) => {
                                return (
                                  <MobileProductCard
                                    key={`m-product-${chunk.id}-${idx}-${chunkIdx}`}
                                    p={chunk}
                                    productOriginMap={productOriginMap}
                                    storeMap={storeMap}
                                    idx={idx}
                                    chunkIdx={chunkIdx}
                                  />
                                );
                              })}
                            </div>
                          );
                        })}
                      </Carousel>
                    )
                  )}
                </div>
              </MaxWidthContainer>
            </div>
          )}

          <MaxWidthContainer className="mt-6 px-4 lg:mt-13">
            <div className="text-xl font-bold text-sorra-gray-900 lg:text-2xl">
              近期用後感
            </div>
          </MaxWidthContainer>
          <div className="mt-6">
            <HomeNewReviews
              isLoading={isNewReviewsLoading}
              reviews={new_reviews}
              speed={20}
            />
          </div>

          {/* 美妝情報 */}
          <HomeLandingInfo />

          {/* miniBanners */}
          <HomeMiniBanner
            isLoading={isMiniBannersLoading}
            banners={miniBanners}
            setActiveIndex={setActiveMiniBannerIndex}
            index={activeMiniBannerIndex}
          />

          {/* sorraFeature2 */}
          {sorraFeaturedTab2.features &&
            sorraFeaturedTab2.features.length > 0 && (
              <div>
                <MaxWidthContainer className="mt-6 px-4 lg:mt-12 lg:px-0">
                  {/* <div className="text-xl font-bold text-[#111928] lg:text-2xl">
                  更多好物雷品發現！
                </div> */}
                  <div className="no-scrollbar mt-6 flex w-full flex-row gap-x-8 overflow-x-auto">
                    {sorraFeaturedTab2.features &&
                      sorraFeaturedTab2.features.map((tab, idx) => {
                        return (
                          <div
                            key={tab.id}
                            className="flex shrink-0 cursor-pointer flex-col gap-y-4"
                            onClick={() => {
                              setSelectedSorraFeatured2(idx);
                              handleSorraFeaturedProduct(
                                'sorra_featured2s',
                                tab.id,
                                ''
                              );
                            }}
                          >
                            <p
                              className={classnames(
                                'text-base lg:text-xl',
                                idx === selectedSorraFeatured2
                                  ? 'font-bold'
                                  : 'font-normal'
                              )}
                            >
                              {tab.name}
                            </p>
                            <div
                              className={classnames(
                                'h-[3px] rounded-full',
                                idx === selectedSorraFeatured2
                                  ? 'bg-sorra-pink'
                                  : 'bg-transparent'
                              )}
                            />
                          </div>
                        );
                      })}
                  </div>
                </MaxWidthContainer>
                <div className="h-px w-full bg-gray-100"></div>

                <MaxWidthContainer
                  key={selectedSorraFeatured2}
                  className="overflow-hidden px-4 pt-5 lg:px-0 lg:pt-8"
                >
                  {isLg ? (
                    <div className="mini-banner-carousel relative hidden lg:block">
                      {selectedSorraFeaturedProducts2.length > 0 && (
                        <Carousel
                          swipeable={true}
                          emulateTouch={true}
                          stopOnHover={true}
                          showIndicators={false}
                          showStatus={false}
                          showThumbs={false}
                          renderArrowPrev={(
                            clickHandler,
                            hasPrev,
                            labelPrev
                          ) => {
                            return (
                              hasPrev && (
                                <button
                                  onClick={clickHandler}
                                  className="absolute -left-6 top-1/2 z-10 h-10 w-10 -translate-x-full -translate-y-1/2 rounded-full"
                                  style={{
                                    boxShadow:
                                      '0px 5px 7.5px -1.25px rgba(0, 0, 0, 0.1), 0px 2.5px 5px -2.5px rgba(0, 0, 0, 0.05)',
                                  }}
                                >
                                  <img
                                    className="p-2.5"
                                    src="/svgs/index-horizontalScroll-arrowLeft.svg"
                                  ></img>
                                </button>
                              )
                            );
                          }}
                          renderArrowNext={(
                            clickHandler,
                            hasNext,
                            labelNext
                          ) => {
                            return (
                              hasNext && (
                                <button
                                  onClick={clickHandler}
                                  className="absolute -right-6 top-1/2 z-10 h-10 w-10 -translate-y-1/2 translate-x-full rounded-full"
                                  style={{
                                    boxShadow:
                                      '0px 5px 7.5px -1.25px rgba(0, 0, 0, 0.1), 0px 2.5px 5px -2.5px rgba(0, 0, 0, 0.05)',
                                  }}
                                >
                                  <img
                                    className="p-2.5"
                                    src="/svgs/index-horizontalScroll-arrowRight.svg"
                                  ></img>
                                </button>
                              )
                            );
                          }}
                        >
                          {chunkSorraFeaturedProducts2.map((p, idx) => {
                            return (
                              <div key={idx} className="grid grid-cols-5 gap-6">
                                {p.map((chunk, chunkIdx) => {
                                  return (
                                    <ProductCard
                                      key={`product-${chunk.id}-${idx}-${chunkIdx}`}
                                      p={chunk}
                                      productOriginMap={productOriginMap}
                                      storeMap={storeMap}
                                      idx={idx}
                                      chunkIdx={chunkIdx}
                                    />
                                  );
                                })}
                              </div>
                            );
                          })}
                        </Carousel>
                      )}
                    </div>
                  ) : (
                    chunkMobileSorraFeaturedProducts2.length > 0 && (
                      <Carousel
                        swipeable={true}
                        emulateTouch={true}
                        stopOnHover={true}
                        showIndicators={false}
                        showStatus={false}
                        showThumbs={false}
                        showArrows={false}
                      >
                        {chunkMobileSorraFeaturedProducts2.map((p, idx) => {
                          return (
                            <div
                              key={idx}
                              className="grid grid-cols-2 gap-2.5 pr-2.5"
                            >
                              {p.map((chunk, chunkIdx) => {
                                return (
                                  <MobileProductCard
                                    key={`m-product-${chunk.id}-${idx}-${chunkIdx}`}
                                    p={chunk}
                                    productOriginMap={productOriginMap}
                                    storeMap={storeMap}
                                    idx={idx}
                                    chunkIdx={chunkIdx}
                                  />
                                );
                              })}
                            </div>
                          );
                        })}
                      </Carousel>
                    )
                  )}
                </MaxWidthContainer>
              </div>
            )}

          <div className="relative mt-10 pb-12 lg:mt-20 lg:pb-16">
            <div className="absolute flex h-full w-full flex-col">
              <MaxWidthContainer className="aspect-[1290/128] shrink-0" />
              <div
                className="grow"
                style={{
                  background:
                    'radial-gradient(132.98% 110.07% at 93.4% 20.5%, rgba(251, 210, 216, 0.30) 3.61%, rgba(255, 237, 239, 0.30) 69.68%)',
                }}
              ></div>
            </div>
            <div className="relative">
              <MaxWidthContainer className="relative">
                <div className="hidden lg:block">
                  <div
                    className="relative aspect-[1290/256] cursor-pointer overflow-hidden rounded-lg"
                    onClick={() => {
                      window.location.href = '/membership';
                    }}
                  >
                    <img
                      className="h-full w-full object-cover"
                      src="/images/desktop_join_member.png"
                    />
                  </div>
                </div>
                <div className="block lg:hidden">
                  <div
                    className="relative aspect-[375/428] cursor-pointer"
                    onClick={() => {
                      window.location.href = '/membership';
                    }}
                  >
                    <img
                      className="h-full w-full object-cover"
                      src="/images/mobile_join_member.png"
                    />
                  </div>
                </div>
              </MaxWidthContainer>

              {newActivities.length > 0 && (
                <MaxWidthContainer className="px-4 pt-6 lg:px-0 lg:pt-11">
                  <div className="text-xl font-bold text-[#111928] lg:text-2xl">
                    會員免費試用
                  </div>
                  <div className="grid grid-cols-1 gap-3 pt-[22px] lg:grid-cols-3 lg:gap-6 lg:pt-7">
                    {newActivities.map((act) => {
                      const { id, name } = act;
                      const thumbnail = _.get(act, 'thumbnail.url');
                      return (
                        <div
                          key={id}
                          // href={`/campaign/${act.name}`}
                          className="flex cursor-pointer flex-col gap-y-4"
                          onClick={() => {
                            window.location.href = `/campaign/${name}`;
                          }}
                        >
                          <div className="relative aspect-[343/193] overflow-hidden rounded-lg lg:aspect-[405/228]">
                            <WebpImg
                              src={thumbnail}
                              className="h-full w-full object-cover"
                            />
                            {id === 10 && (
                              <img
                                className="h-px w-px"
                                src="https://ad.doubleclick.net/ddm/trackimp/N1003017.4334518SORRAHKFACEBOOK/B32685117.406872372;dc_trk_aid=598558842;dc_trk_cid=224181113;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;ltd=;dc_tdv=1"
                              />
                            )}
                            {id === 12 && (
                              <img
                                className="h-px w-px"
                                src="https://ad.doubleclick.net/ddm/trackimp/N1003017.4334518SORRAHKFACEBOOK/B32821884.407904241;dc_trk_aid=599933411;dc_trk_cid=225687372;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;ltd=;dc_tdv=1"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-center pt-6 lg:pt-7">
                    <a
                      className="block w-full rounded-lg border border-sorra-pink px-5 py-2.5 text-center text-sm font-medium text-sorra-pink focus:outline-none focus:ring-4 focus:ring-blue-300 xl:w-[360px]"
                      href="/campaign"
                    >
                      查看全部
                    </a>
                  </div>
                </MaxWidthContainer>
              )}

              {rewards.length > 0 && (
                <MaxWidthContainer className="px-4 pt-6 lg:px-0 lg:pt-11">
                  <div className="text-xl font-bold text-[#111928] lg:text-2xl">
                    免費換領
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-[22px] lg:grid-cols-4 lg:gap-6 lg:pt-7">
                    {rewards.map((reward) => {
                      const { inventory, score, original_score } = reward;
                      return (
                        <div
                          key={reward.id}
                          className={classnames(
                            'flex flex-col',
                            inventory > 0 ? 'cursor-pointer' : ''
                          )}
                          onClick={() => {
                            if (inventory > 0) {
                              window.location.href = `/rewards/gift/${reward.id}`;
                            }
                          }}
                        >
                          <div className="aspect-square overflow-hidden rounded-lg border border-sorra-gray-100 bg-white">
                            <WebpImg
                              src={
                                getProductWebpImageUrl(_.get(reward, 'image'))
                                // _.get(reward, 'image.url') ||
                                // `${window.location.origin}/images/placeholders/product.png`
                              }
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="mt-6 flex flex-col gap-y-1">
                            {_.get(reward, 'brand.name') && (
                              <p className="text-sm text-gray-800">
                                {reward.brand.name}
                              </p>
                            )}
                            {_.get(reward, 'en_name') && (
                              <p className="text-base font-medium text-[#111928] lg:text-lg">
                                {reward.name}
                              </p>
                            )}
                            {_.get(reward, 'name') && (
                              <p className="text-base font-medium text-[#111928] lg:text-lg">
                                {reward.en_name}
                              </p>
                            )}
                          </div>
                          <div className="grow" />
                          <div className="mt-4 min-h-12">
                            <div className="flex">
                              <img
                                src="/svgs/sorra-points-small.svg"
                                className="mr-2"
                              ></img>
                              <div className="flex gap-x-2 text-base font-medium text-sorra-gray-900">
                                <p
                                  className={
                                    reward.original_score > 0
                                      ? 'text-sorra-pink'
                                      : ''
                                  }
                                >
                                  {'$' + reward.score}
                                </p>
                                {reward.original_score > 0 && (
                                  <p className="font-normal line-through">{`$${reward.original_score}`}</p>
                                )}
                              </div>
                            </div>
                            <div
                              className={
                                'mt-0.5 text-xs font-medium text-sorra-gray-800' +
                                (inventory == 0 || inventory >= 2
                                  ? ' hidden'
                                  : '')
                              }
                            >
                              剩餘最後{' '}
                              <span className="text-sorra-pink">
                                {inventory}
                              </span>{' '}
                              件產品
                            </div>
                            <div
                              className={
                                'mt-0.5 text-xs font-medium text-sorra-gray-800' +
                                (inventory > 0 ? ' hidden' : '')
                              }
                            >
                              換完了～ 請期待新一輪獎賞！
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-center pt-6 lg:pt-7">
                    <a
                      className="block w-full rounded-lg border border-sorra-pink px-5 py-2.5 text-center text-sm font-medium text-sorra-pink focus:outline-none focus:ring-4 focus:ring-blue-300 xl:w-[360px]"
                      href="/rewards"
                    >
                      查看全部
                    </a>
                  </div>
                </MaxWidthContainer>
              )}
            </div>
          </div>
          <HomeSignupSteps />
          <div className="pt-6 lg:pt-20">
            <MaxWidthContainer className="p-4">
              <div className="text-2xl font-bold text-sorra-gray-900">
                社群文章
              </div>
            </MaxWidthContainer>

            <MaxWidthContainer className="grid grid-cols-2 gap-x-6 gap-y-6 px-4 lg:grid-cols-4 lg:gap-y-0 lg:px-0">
              {_.filter(posts, (post) => {
                const ogImage = post.image_url || '';
                return ogImage != '' > 0;
              })
                .slice(0, 4)
                .map((post, idx) => {
                  const { id, title, url, category_name, image_url } = post;
                  const imageUrl = image_url || '';
                  const categoryName = category_name || '';

                  return (
                    <a key={`${idx}:${id}`} className="block" href={url}>
                      <img
                        className="aspect-square w-full rounded-xl object-cover"
                        src={imageUrl?.url}
                      ></img>
                      <div className="pt-6 text-sm text-sorra-gray-800">
                        {categoryName}
                      </div>
                      <div className="line-clamp-2 pt-1 text-lg font-medium text-sorra-gray-900">
                        {title.replaceAll('&amp;', '&')}
                      </div>
                    </a>
                  );
                })}
            </MaxWidthContainer>

            <MaxWidthContainer className="flex items-center justify-center p-4 pt-8">
              <a
                type="button"
                className="mb-2 mr-2 w-full rounded-lg border border-sorra-pink px-5 py-2.5 text-center text-sm font-medium text-sorra-pink focus:outline-none focus:ring-4 focus:ring-blue-300 xl:w-[360px]"
                href="https://www.sorra.net/blog/"
              >
                查看全部
              </a>
            </MaxWidthContainer>
          </div>
          <div className="pt-6 lg:pt-20">
            <MaxWidthContainer className="p-4">
              <div className="text-2xl font-bold text-sorra-gray-900">
                合作優惠
              </div>
            </MaxWidthContainer>

            <MaxWidthContainer className="grid grid-cols-2 gap-x-6 gap-y-6 px-4 pt-8 lg:grid-cols-4 lg:px-0">
              {/* {promotions} */}
              {promotions.map((promotion) => {
                const { id, item_codename } = promotion;
                return (
                  <a
                    key={id}
                    className="flex gap-x-1 text-sm text-sorra-gray-900"
                    href={/*element.url*/ 'rewards?tab=code'}
                  >
                    <img
                      src="/svgs/index-partnership-arrow.svg"
                      className="mb-[auto] mt-[2px]"
                    ></img>
                    {item_codename}
                  </a>
                );
              })}
              {/* {blockPromotionsState} */}
            </MaxWidthContainer>
          </div>
          <div className="pt-6 lg:pt-20">
            <MaxWidthContainer className="p-4">
              <div className="text-2xl font-bold text-sorra-gray-900">
                搜尋熱門美妝護膚分類
              </div>
            </MaxWidthContainer>

            <MaxWidthContainer className="p-4 text-sm text-sorra-gray-900">
              {secondCategories.map((sc, idx) => {
                const { id, name } = sc;
                return (
                  <React.Fragment key={id}>
                    <span>{idx > 0 ? ' | ' : ''}</span>
                    <a
                      href={
                        '/search?product=&category=' +
                        name +
                        '&categoryID=' +
                        id +
                        '&keyword='
                      }
                    >
                      {name}
                    </a>
                  </React.Fragment>
                );
              })}
            </MaxWidthContainer>
          </div>
          <div className="pt-6 lg:pt-20">
            <MaxWidthContainer className="p-4">
              <div
                className="cursor-pointer items-center border-b-2 border-t-2 border-gray-100 py-6"
                onClick={() =>
                  setBottomSuggestionsExpanded(
                    bottomSuggestionsExpanded == true ? false : true
                  )
                }
              >
                <div className="flex">
                  <div className="text-2xl font-bold text-sorra-gray-900">
                    化妝護膚品推薦
                  </div>
                  <div className="bottom-1 right-0 ml-[auto] text-sm font-bold">
                    <img
                      src="/svgs/index-section-expand.svg"
                      className={
                        bottomSuggestionsExpanded == true ? 'hidden' : ''
                      }
                    ></img>
                    <img
                      src="/svgs/index-section-collapse.svg"
                      className={
                        bottomSuggestionsExpanded == true ? '' : 'hidden'
                      }
                    ></img>
                  </div>
                </div>

                <div
                  className={
                    'pt-6' +
                    (bottomSuggestionsExpanded == true ? '' : ' hidden')
                  }
                >
                  <Markdown>{seo_recommend || ''}</Markdown>
                </div>
              </div>

              <div
                className="cursor-pointer items-center border-b-2 border-gray-100 py-6"
                onClick={() =>
                  setBottomFaqExpanded(bottomFaqExpanded == true ? false : true)
                }
              >
                <div className="flex">
                  <div className="text-2xl font-bold text-sorra-gray-900">
                    常見問題
                  </div>
                  <div className="bottom-1 right-0 ml-[auto] text-sm font-bold">
                    <img
                      src="/svgs/index-section-expand.svg"
                      className={bottomFaqExpanded == true ? 'hidden' : ''}
                    ></img>
                    <img
                      src="/svgs/index-section-collapse.svg"
                      className={bottomFaqExpanded == true ? '' : 'hidden'}
                    ></img>
                  </div>
                </div>

                <div
                  className={
                    'pt-6' + (bottomFaqExpanded == true ? '' : ' hidden')
                  }
                >
                  <Markdown>{seo_question || ''}</Markdown>
                </div>
              </div>
            </MaxWidthContainer>
          </div>
          <div className="pt-6 lg:pt-24"></div>
          <CommentPopup
            externalPopupShown={isCommentModalShow}
            externalProductID={null}
            externalProductBrandName={null}
            externalProductName={null}
            setExternalPopupShown={setIsCommentModalShow}
          />
          <Footer />
          <MobileTabBar props={{ highlighted: 'home' }} />
        </div>
      </main>
    </div>
  );
};

export default Home;

export async function getServerSideProps(ctx) {
  const defaultTitle =
    '護膚品好用推介/比較 | 化妝品推薦香港 | 最真實化妝護膚品評價 - Sorra';
  const defaultDescription =
    '尋找護膚品好用的推薦和比較？Sorra提供最詳實的產品評論、價格比較、成份分析和用戶意見。我們根據用戶評價和專業評測，比較各種化妝品和護膚品，幫助您做出明智的選擇。無論您需要什麼，從化妝品到護膚品，我們的平台都有您需要的資訊。立即探索，找到最適合您的產品！';

  try {
    const { data: homPageSeo } = await api.request({
      method: 'GET',
      url: `/api/seo/homePage`,
    });
    const {
      page_title: pageTitle = '',
      page_meta_description: pageDescription = '',
    } = homPageSeo;

    const title = pageTitle || defaultTitle;
    const description = pageDescription || defaultDescription;

    return {
      props: {
        seo: { title, description },
      },
    };
  } catch (e) {
    return {
      props: {
        seo: {
          title: defaultTitle,
          description: defaultDescription,
        },
      },
    };
  }
}
